import { useOverlays } from "contexts/OverlayContext";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { OrganizationIdNameLogoUrl } from "@common/schemas/organization.schema";
import { PlainModal } from "@design-system/Modal/Modal";
import { ConfirmOrganizationsModal } from "./ConfirmOrganizationsModal/ConfirmOrganizationsModal";

interface MemberManagementConfirmOrganizationsOverlayData {
    organizations: OrganizationIdNameLogoUrl[];
    onConfirm: () => Promise<void>;
    onBackToAddMember?: Record<string, unknown>;
    onClose?: () => void;
}

interface MemberManagementConfirmOrganizationsContentProps {
    overlayData: MemberManagementConfirmOrganizationsOverlayData;
    closeOverlay: () => void;
    openOverlay: ReturnType<typeof useOverlays>["openOverlay"];
    overlayIsClosing: boolean;
}

const MemberManagementConfirmOrganizationsContent = ({
    overlayData,
    closeOverlay,
    openOverlay,
    overlayIsClosing,
}: MemberManagementConfirmOrganizationsContentProps) => {
    const { error: errorToast } = useToaster();
    const { organizations, onConfirm, onBackToAddMember, onClose } =
        overlayData;

    const handleClose = () => {
        closeOverlay();
        onClose?.();
    };

    return (
        <PlainModal show={!overlayIsClosing} onHide={handleClose}>
            <ConfirmOrganizationsModal
                organizations={organizations}
                onCancel={() => {
                    if (onBackToAddMember) {
                        openOverlay(
                            "memberManagementAddMember",
                            onBackToAddMember,
                        );
                    } else {
                        handleClose();
                    }
                }}
                onConfirm={async () => {
                    try {
                        await onConfirm();
                        handleClose();
                    } catch {
                        errorToast(
                            "Failed to add the member. Please try again.",
                        );
                    }
                }}
            />
        </PlainModal>
    );
};

export const MemberManagementConfirmOrganizationsModalOverlay = () => {
    const { getOverlayInfo, openOverlay, closeOverlay, overlayIsClosing } =
        useOverlays();
    const overlayInfo = getOverlayInfo("memberManagementConfirmOrganizations");

    if (!overlayInfo?.data) {
        return null;
    }

    return (
        <MemberManagementConfirmOrganizationsContent
            overlayData={overlayInfo.data}
            closeOverlay={closeOverlay}
            openOverlay={openOverlay}
            overlayIsClosing={overlayIsClosing}
        />
    );
};
